import React, { useState } from 'react';
import axios from 'axios';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import { withStyles } from '@material-ui/core/styles';
import moment from 'moment';

import CustomDatePickerMui from '../digitization/dashboard/CustomDatePickerMui';
import { appUrl } from '../../../../utils/config';

const styles = () => ({
  formContainer: {
    padding: '1.5rem',
  },
  submitBtn: {
    backgroundColor: '#3f51b5',
    color: '#ffffff',
    textTransform: 'none',
    marginRight: '1rem',
    '&:hover': {
      backgroundColor: '#303f9f',
    },
    '&:disabled': {
      backgroundColor: '#c5cae9',
    },
  },
  resetBtn: {
    textTransform: 'none',
  },
  resultCard: {
    marginTop: '1.5rem',
    border: '1px solid #e0e0e0',
  },
  successCard: {
    backgroundColor: '#e8f5e9',
    border: '1px solid #4caf50',
  },
  errorCard: {
    backgroundColor: '#ffebee',
    border: '1px solid #f44336',
  },
  fieldLabel: {
    fontWeight: 500,
    marginBottom: '0.25rem',
  },
});

const ENDPOINTS = {
  syncPO: 'syncSASLDataNew1',
  updatePO: 'updateSASLDSDataNew',
  syncInventory: 'syncInventoryDataNew',
};

const SyncForm = ({ classes, type }) => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [vendorNumber, setVendorNumber] = useState('');
  const [vendorPlant, setVendorPlant] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [materialNumber, setMaterialNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const isInventory = type === 'syncInventory';

  const buildUrl = () => {
    const basePath = `${appUrl}/Pharma%20Contract%20Manufacturing/n1/sh1/Pharma%20Co/Admin`;
    return `${basePath}/${ENDPOINTS[type]}`;
  };

  const buildPayload = () => {
    const dateRanges = [
      {
        startDate: moment(startDate).format('YYYY-MM-DDT00:00:01[Z]'),
        endDate: moment(endDate).format('YYYY-MM-DDT23:59:59[Z]'),
      },
    ];

    if (isInventory) {
      return {
        dateRanges,
        vendorNumber: vendorNumber ? [vendorNumber.trim()] : [],
        vendorPlant: vendorPlant ? [vendorPlant.trim()] : [],
        storageLocation: [],
        materialNumber: materialNumber ? [materialNumber.trim()] : [],
        materialTypes: [],
        InitDump: [],
        inventoryBatchNumber: [],
      };
    }

    return {
      dateRanges,
      vendorNumber: vendorNumber ? [vendorNumber.trim()] : [],
      vendorPlant: vendorPlant ? [vendorPlant.trim()] : [],
      storageLocation: [''],
      poNumber: poNumber ? [poNumber.trim()] : [],
    };
  };

  const handleSubmit = async () => {
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const response = await axios.post(buildUrl(), buildPayload());
      setResult(response.data);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          'An error occurred while processing the request.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setStartDate(null);
    setEndDate(null);
    setVendorNumber('');
    setVendorPlant('');
    setPoNumber('');
    setMaterialNumber('');
    setResult(null);
    setError(null);
  };

  const isFormValid =
    startDate &&
    endDate &&
    vendorNumber.trim() &&
    vendorPlant.trim() &&
    (!isInventory || materialNumber.trim());

  return (
    <div className={classes.formContainer}>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant='body2' className={classes.fieldLabel}>
            Start Date *
          </Typography>
          <CustomDatePickerMui
            name='startDate'
            value={startDate}
            onChange={(date) => setStartDate(date)}
            placeholder='Select start date'
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant='body2' className={classes.fieldLabel}>
            End Date *
          </Typography>
          <CustomDatePickerMui
            name='endDate'
            value={endDate}
            onChange={(date) => setEndDate(date)}
            placeholder='Select end date'
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant='body2' className={classes.fieldLabel}>
            Vendor Number *
          </Typography>
          <TextField
            variant='outlined'
            size='small'
            fullWidth
            value={vendorNumber}
            onChange={(e) => setVendorNumber(e.target.value)}
            placeholder='e.g. 1257384'
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant='body2' className={classes.fieldLabel}>
            Vendor Plant *
          </Typography>
          <TextField
            variant='outlined'
            size='small'
            fullWidth
            value={vendorPlant}
            onChange={(e) => setVendorPlant(e.target.value)}
            placeholder='e.g. 2091'
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant='body2' className={classes.fieldLabel}>
            {isInventory ? 'Material Number *' : 'PO Number'}
          </Typography>
          <TextField
            variant='outlined'
            size='small'
            fullWidth
            value={isInventory ? materialNumber : poNumber}
            onChange={(e) =>
              isInventory
                ? setMaterialNumber(e.target.value)
                : setPoNumber(e.target.value)
            }
            placeholder={isInventory ? 'e.g. 1439545' : 'e.g. 45111023'}
          />
        </Grid>
      </Grid>

      <Grid
        container
        spacing={2}
        style={{ marginTop: '1.5rem' }}
        alignItems='center'
      >
        <Grid item>
          <Button
            variant='contained'
            className={classes.submitBtn}
            onClick={handleSubmit}
            disabled={!isFormValid || loading}
          >
            {loading ? (
              <CircularProgress size={20} style={{ color: '#ffffff' }} />
            ) : (
              'Submit'
            )}
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant='outlined'
            className={classes.resetBtn}
            onClick={handleReset}
            disabled={loading}
          >
            Reset
          </Button>
        </Grid>
      </Grid>

      {result && (
        <Card
          className={`${classes.resultCard} ${
            result.success ? classes.successCard : classes.errorCard
          }`}
        >
          <CardContent>
            <Typography variant='subtitle1' style={{ fontWeight: 600 }}>
              {result.success ? 'Success' : 'Failed'}
            </Typography>
            <Typography variant='body2'>
              {result.message || 'Operation completed.'}
            </Typography>
            {result.totalRecordsFetched !== undefined && (
              <Typography variant='body2'>
                Records Fetched: {result.totalRecordsFetched}
              </Typography>
            )}
            {result.totalRecordsSaved !== undefined && (
              <Typography variant='body2'>
                Records Saved: {result.totalRecordsSaved}
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {error && (
        <Card className={`${classes.resultCard} ${classes.errorCard}`}>
          <CardContent>
            <Typography
              variant='subtitle1'
              style={{ fontWeight: 600, color: '#f44336' }}
            >
              Error
            </Typography>
            <Typography variant='body2'>{error}</Typography>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default withStyles(styles)(SyncForm);
